import { getConditionalObject } from 'utils/data';

export const surfaceDrawerStyles = {
  // The review surface opens from the compact card *inside* the run-detail
  // AdminSlideDrawer, which renders at the MUI modal z-index (1300). Without an
  // explicit higher layer this drawer mounts behind it and is fully occluded.
  zIndex: 1310,
  '& .MuiDrawer-paper': {
    width: '1200px',
    maxWidth: '95vw',
  },
};

// EntityFormModal clips its body wrapper (height: 100%; overflow: hidden) and
// expects the injected Content to own its own scroll. Fill that bounded,
// position: relative wrapper and scroll internally — otherwise a tall stack of
// Decision sections overflows and is silently clipped.
//
// flexShrink: 0 on the children is load-bearing: this is now a definite-height
// flex column, and each section is a PageBlock whose CardContent is
// `flex: 1; overflow: hidden` — which sets its min-height floor to 0. Without
// pinning the children, the flex algorithm compresses every card down to its
// header and the field grids collapse to nothing. Pinning keeps each section at
// its natural height so the container scrolls instead of squashing them.
export const surfaceContentStyles = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflowY: 'auto',
  paddingRight: 1,
  '& > *': {
    flexShrink: 0,
  },
};

export const surfaceTitleStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  padding: '20px 24px',
};

export const schemaWarningStyles = ({ palette }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: 1.5,
  borderRadius: 1,
  border: `1px solid ${palette.warning.main}`,
  backgroundColor: palette.warning.light,
});

export const sectionGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
};

// isFullWidth makes the field card span both grid columns (repeating tables and
// lone-field sections) instead of being boxed into one half-width cell.
export const getFieldRowStyles =
  ({ isFullWidth }) =>
  ({ palette }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    padding: 1.5,
    borderRadius: 1,
    border: `1px solid ${palette.divider}`,
    ...getConditionalObject(isFullWidth, { gridColumn: '1 / -1' }),
  });

export const fieldRowHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  justifyContent: 'space-between',
};

export const fieldRowLabelStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  minWidth: 0,
};

export const fieldRowActionsStyles = {
  display: 'flex',
  gap: 0.75,
  flexWrap: 'wrap',
};

export const overrideEditorStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

export const overrideEditorActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
};

export const footerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  justifyContent: 'flex-end',
  width: '100%',
  flexWrap: 'wrap',
};

export const changedBadgeStyles = ({ palette }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  padding: '2px 8px',
  borderRadius: 1,
  border: `1px solid ${palette.warning.main}`,
  backgroundColor: palette.warning.light,
});

export const unmappedFieldRowStyles = ({ palette }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  padding: 1.5,
  borderRadius: 1,
  border: `1px dashed ${palette.warning.main}`,
});

export const unmappedNoteStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
};

export const signOffSummaryStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  width: '100%',
};

export const signOffCountsStyles = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap',
};

export const signOffNoticeStyles = ({ palette }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: 1.5,
  borderRadius: 1,
  border: `1px solid ${palette.warning.main}`,
  backgroundColor: palette.warning.light,
});

export const signOffActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  justifyContent: 'flex-end',
};
